import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { USER_ROLES } from '../utils/roleUtils'
import PendingApproval from '../pages/PendingApproval'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

/**
 * Componente que protege rutas basado en roles de usuario
 */
function ProtectedRoute({ 
  children, 
  requiredRole = USER_ROLES.USER, 
  requiredPermission = null,
  fallback = null 
}) {
  const { user, loading, hasMinimumRole, hasPermission, isPendingApproval } = useAuth()

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Si no hay usuario, esto debería manejarse por el App.jsx
  if (!user) {
    return null
  }

  // Si el usuario está pendiente de aprobación
  if (isPendingApproval()) {
    return <PendingApproval />
  }

  // Verificar si el usuario tiene el rol mínimo requerido
  if (requiredRole && !hasMinimumRole(requiredRole)) {
    return fallback || <AccessDenied />
  }

  // Verificar si el usuario tiene el permiso específico requerido
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || <AccessDenied />
  }

  // El usuario tiene acceso, renderizar los children
  return children
}

/**
 * Componente que se muestra cuando el usuario no tiene permisos suficientes
 */
function AccessDenied() {
  const { user, logout } = useAuth()

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            className="h-12 w-auto"
            src="/logo.png"
            alt="Domus"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Acceso Denegado
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Sin Permisos Suficientes
            </h3>
            
            <p className="mt-2 text-sm text-gray-600">
              Hola <span className="font-medium">{user?.name || user?.email}</span>,
              no tienes permisos para acceder a esta página.
            </p>
            
            <div className="mt-4 p-4 bg-red-50 rounded-md">
              <div className="text-sm text-red-700">
                <p>
                  Tu rol actual no te permite ver este contenido. 
                  Si crees que esto es un error, contacta al administrador.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col space-y-3">
              <button
                type="button"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => window.history.back()}
              >
                Reintentar
              </button>
              
              <button
                type="button"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={logout}
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProtectedRoute